// ---------------------------------------------------------------------------
// sim.js -- pure simulation. No DOM, no canvas, no timing assumptions.
//
// Units are millimetres of key travel. depth 0 = fully released (key at top),
// depth = params.travel = bottomed out. Each switch owns its own travel, so a
// 4.0 mm switch and a 3.5 mm switch can be compared side by side: the finger
// motion is a single absolute displacement in mm, and each switch clamps it at
// its own bottom-out point.
// ---------------------------------------------------------------------------

// Ceiling for the travel fields. Not a physical constant, just a UI bound.
const MAX_TRAVEL = 6.0;

// Firmware limit on where the release point may sit: at least this far below
// bottom-out, and at least this far below the actuation point. Travel 3.4 ->
// release at most 3.3; actuation 0.7 -> release at least 0.8. The UI bounds
// and the simulation's own guard both derive from this one number.
const RELEASE_GAP = 0.1;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// The sense zone is drawn easing toward its anchor rather than snapping to it.
// Measured off the reference animation at ~175 ms to 1/e; the sim steps at 1 ms.
const SENSE_EASE_MS = 175;
const SENSE_EASE_PER_STEP = 1 - Math.exp(-1 / SENSE_EASE_MS);

// A stock sense zone is a few hundredths of a millimetre -- a hairline at any
// honest scale, and invisible as a mechanism. The zone is therefore widened to
// at least this fraction of total travel, and the switch is driven by that
// widened value: what is drawn is exactly what triggers, so the animation never
// shows one thing and does another. The configured `sense` still governs once
// it exceeds the floor.
const SENSE_DISPLAY_MIN_FRAC = 0.06;

// ... but never wider than this fraction of the rapid trigger band. The
// widening exists to make the mechanism visible, and a zone wider than the
// band it works in makes the mechanism impossible instead: a stroke has to
// move more than half the zone off its anchor to toggle, and inside a 0.1 mm
// band (actuation 0.7, release 0.8) a 0.204 mm zone can never be escaped, so
// the key sat on from first actuation until the finger lifted clear -- reading
// as "always on". At 0.35 of the band a stroke can fit inside the band, clear
// the zone, and still keep clear of a regular threshold sitting mid-band (see
// wiggleRoom). The configured `sense` is never reduced by this; if it
// genuinely exceeds the band, that is what the hardware would do too.
const SENSE_DISPLAY_MAX_BAND_FRAC = 0.35;

// --- Regular switch: a single fixed threshold ------------------------------
// ON at or below `actuation`, OFF above it. One point, no hysteresis: the key
// resets at exactly the same depth it actuates. If the finger never lifts back
// above that one point, the key never re-fires.
class RegularSwitch {
  constructor(params) { this.params = params; this.reset(); }

  reset() {
    this.on = false; this.presses = 0; this.depth = 0;
    // Milliseconds since the last press. The sim steps at 1 ms, so this is a
    // plain counter -- and because it lives in the switch rather than in the
    // renderer, the press flash is deterministic and survives GIF export.
    this.msSincePress = 1e9;
  }

  step(rawDepth) {
    const depth = Math.min(rawDepth, this.params.travel); // bottom out
    this.depth = depth;
    this.msSincePress++;

    const shouldBeOn = depth >= this.params.actuation;
    if (shouldBeOn && !this.on) { this.presses++; this.msSincePress = 0; }
    this.on = shouldBeOn;
  }

  markers() {
    return [{ key: 'actuation', depth: this.params.actuation }];
  }

  zones() { return []; }
}

// --- Analog (hall effect) switch with Rapid Trigger ------------------------
// Three zones, top to bottom:
//
//   depth <  actuation            ALWAYS OFF.  Above the initial actuation
//                                 point nothing can turn the key on.
//   actuation <= depth < release  RAPID TRIGGER BAND. The reset threshold
//                                 stops being fixed and trails the travel. The
//                                 key is compared against the last accepted
//                                 position: move more than half the sense zone
//                                 deeper and it turns ON, more than half of it
//                                 shallower and it turns OFF. Anything smaller
//                                 is ignored -- that ignored window IS the
//                                 SENSE ZONE.
//   depth >= release              ALWAYS ON. Deep enough that the press is
//                                 unambiguous, so RT wiggle logic is
//                                 suppressed and the key is held on.
class AnalogSwitch {
  constructor(params) { this.params = params; this.reset(); }

  reset() {
    this.on = false;
    this.armed = false;
    this.zone = 'above';
    this.ref = 0;       // last accepted position -- the sense zone's anchor
    this.shownRef = 0;  // eased copy of `ref`, for drawing only
    this.presses = 0;
    this.depth = 0;
    this.msSincePress = 1e9;   // see RegularSwitch.reset
  }

  // The release point as the firmware would accept it: no closer than
  // RELEASE_GAP to the actuation point or to bottom-out. Also guards against a
  // half-typed value inverting the zones.
  get releasePoint() {
    const p = this.params;
    return clamp(p.release, p.actuation + RELEASE_GAP, p.travel - RELEASE_GAP);
  }

  // Height of the rapid trigger band: actuation point to release point.
  get bandWidth() {
    return Math.max(0, this.releasePoint - this.params.actuation);
  }

  // The sense width actually in force: the configured value, widened to the
  // display floor -- capped so the widening can never outgrow the band it has
  // to work in. Both the trigger logic and the drawn box use this.
  get effectiveSense() {
    const floor = Math.min(SENSE_DISPLAY_MIN_FRAC * this.params.travel,
                           SENSE_DISPLAY_MAX_BAND_FRAC * this.bandWidth);
    return Math.max(this.params.sense, floor);
  }

  step(rawDepth) {
    const p = this.params;
    const depth = Math.min(rawDepth, p.travel); // bottom out
    this.depth = depth;
    this.msSincePress++;
    const release = this.releasePoint;

    // --- The sense rule, run everywhere in the travel.
    // The key position is compared against the last accepted position. If it
    // has not moved further than the sensitivity, the movement is ignored and
    // the anchor stays put -- that standing anchor is what makes the sense
    // zone a dead zone rather than a line. Escaping it re-anchors on wherever
    // the key now is.
    const half = this.effectiveSense / 2;
    let escaped = 0;
    if (depth > this.ref + half) { escaped = +1; this.ref = depth; }
    else if (depth < this.ref - half) { escaped = -1; this.ref = depth; }

    // Ease the drawn anchor toward the real one. Rendering only -- it never
    // feeds back into the state below.
    this.shownRef += (this.ref - this.shownRef) * SENSE_EASE_PER_STEP;

    // Zone 1 -- above the actuation point: always off.
    if (depth < p.actuation) {
      this.zone = 'above';
      this.on = false;
      this.armed = false;
      return;
    }

    // Zone 3 -- below the release point: always on.
    if (depth >= release) {
      this.zone = 'below';
      if (!this.on) { this.on = true; this.presses++; this.msSincePress = 0; }
      this.armed = true;
      return;
    }

    // Zone 2 -- the rapid trigger band, the only place the sense rule is
    // allowed to change the output.
    this.zone = 'band';

    // First entry into the band from above is the initial actuation.
    if (!this.armed) {
      this.armed = true;
      this.on = true;
      this.presses++;
      this.msSincePress = 0;
      this.ref = depth;
      return;
    }

    if (escaped > 0 && !this.on) { this.on = true; this.presses++; this.msSincePress = 0; }
    else if (escaped < 0 && this.on) { this.on = false; }
  }

  markers() {
    return [
      { key: 'actuation', depth: this.params.actuation },
      { key: 'release',   depth: this.releasePoint },
    ];
  }

  // The SENSE ZONE: the dynamic dead zone that follows the key's travel.
  // Movement smaller than the sensitivity is ignored, so the key only changes
  // state once it leaves this band. `effectiveSense` is its full height, split
  // evenly either side of the anchor, so it is symmetric and centred on the key.
  //
  // Drawn everywhere in the travel, as the reference animation does. Outside
  // the rapid trigger band it is still sensing; it just isn't allowed to
  // change the output there.
  senseZone() {
    const half = this.effectiveSense / 2;
    return { from: this.shownRef - half, to: this.shownRef + half };
  }

  // Shaded region marking where rapid trigger is live.
  zones() {
    return [{ from: this.params.actuation, to: this.releasePoint, kind: 'rt' }];
  }
}

// --- Motion presets: depth(t, ctx) in mm, t in seconds ---------------------
// Display names live in i18n.js, keyed by the preset key.
// This is the finger driving both switches. Both always see the exact same
// signal -- that is the claim the side-by-side is making.
//
// `ctx` describes the stage the motion is performing on:
//   span                    the deeper of the two travels
//   bandTop / bandBottom    the analog switch's actuation and release points
//   bottom                  the analog switch's bottom-out depth
//   sense                   the sense zone width actually in force
//   regularActuation        the regular switch's single threshold
//
// Presets are written against those rather than against absolute millimetres,
// so they still land where they are meant to when the config changes. `ctx` is
// derived from configured parameters only, never from live switch state --
// the finger must not react to what the switches are doing.
const smoothstep = x => { const c = clamp(x, 0, 1); return c * c * (3 - 2 * c); };

// Amplitude for a wiggle that is meant to work rapid trigger.
//
// Geometry alone is not enough: a stroke only escapes the sense zone if it
// moves more than half of it off the anchor, so an amplitude chosen purely as
// a fraction of the band demonstrates nothing once the sense zone is widened.
// `pref` is what the preset wants on geometric grounds, `room` is the most it
// can use without leaving its zone. The sense floor wins over `pref`, and
// `room` wins over both -- if the zone is too small to hold a working stroke
// the preset degrades honestly rather than silently drawing a lie.
function wiggleAmp(pref, sense, room) {
  return clamp(Math.max(pref, sense * 0.75), 0, Math.max(room, 0));
}

// How much of the band a preset lets its wiggle use, as an amplitude. `frac`
// is the share it would keep to on geometric grounds -- small, so that
// Multiple Actuations has room for three distinct stops. When that share is
// too small to hold a stroke that escapes the sense zone the share grows,
// because a wiggle that cannot toggle demonstrates nothing; it stops growing
// at BAND_ROOM_MAX, past which the stroke would leave the band. In a 0.1 mm
// band with a 0.06 mm sense zone the geometric 22% (0.022 mm) is under the
// 0.03 mm needed to toggle; this lets it out to 0.045 mm.
const BAND_ROOM_MAX = 0.45;
function bandRoom(frac, width, sense) {
  return Math.min(Math.max(frac * width, sense * 0.75), BAND_ROOM_MAX * width);
}

// The room a preset's wiggle gets inside the band, with the regular switch's
// threshold dodged (see dodgeRoom below) when it lies in [lo, hi]. The dodge
// is dropped if honouring it would leave less than a toggling stroke: a
// regular switch chattering beside a working rapid trigger is a weaker demo,
// but a rapid trigger that never fires is no demo at all.
function wiggleRoom(frac, width, ctx, lo, hi) {
  const room = bandRoom(frac, width, ctx.sense);
  const dodged = Math.min(room, dodgeRoom(ctx.regularActuation, lo, hi));
  return dodged >= ctx.sense * 0.55 ? dodged : room;
}

// Choose a wiggle centre inside [lo, hi] that keeps the whole stroke clear of
// the regular switch's single threshold.
//
// A stroke straddling that threshold makes the regular switch chatter once per
// cycle, right alongside rapid trigger -- which destroys the contrast the
// preset exists to show. Preference is to sit wholly deeper than the threshold
// so the regular switch stays on for the whole wiggle (one press); failing
// that, wholly shallower (no presses). Either reads as a clean comparison.
// Clearance a dodged wiggle keeps between the threshold and the near edge of
// its stroke, as a multiple of amplitude. Shared by the two functions below so
// the amplitude cap and the placement agree exactly -- when they disagreed by a
// constant the dodge silently failed by a hundredth of a millimetre.
const DODGE_PAD = 1.15;

// The largest amplitude that still leaves room for `clearOfThreshold` to place
// a wiggle wholly on one side of the threshold inside [lo, hi]. Infinity when
// the threshold is outside that span and there is nothing to dodge. The 0.98
// keeps the placement comfortably inside rather than exactly on the boundary.
function dodgeRoom(threshold, lo, hi) {
  if (threshold <= lo || threshold >= hi) return Infinity;
  return Math.max(hi - threshold, threshold - lo) / (1 + DODGE_PAD) * 0.98;
}

function clearOfThreshold(pref, amp, threshold, lo, hi) {
  if (hi <= lo) return pref;
  const pad = amp * DODGE_PAD;
  if (threshold < pref - pad || threshold > pref + pad) return clamp(pref, lo, hi);
  const deeper = threshold + pad;
  if (deeper <= hi) return deeper;
  const shallower = threshold - pad;
  if (shallower >= lo) return shallower;
  return clamp(pref, lo, hi);
}

const MOTIONS = {
  'multi-actuation': {
    period: 6.8,
    blurb: 'Step down the travel, wiggling at each stop. The regular switch fires once on the way past its actuation point; rapid trigger re-actuates at every stop, at any depth.',
    depth: (t, ctx) => {
      const W = Math.max(0, ctx.bandBottom - ctx.bandTop);

      // Amplitude first, because it decides how much of the band each stop has
      // to clear at top and bottom. Stops keep a margin from the band edges
      // over and above the amplitude, so the dodge is computed against the
      // band less that margin -- against the full band it could hand back an
      // amplitude for which no clear placement exists, and the wiggle would
      // straddle the regular threshold after all.
      const margin = 0.04 * W;
      const amp = wiggleAmp(0.035 * ctx.span, ctx.sense,
        wiggleRoom(0.22, W, ctx, ctx.bandTop + margin, ctx.bandBottom - margin));
      const pad = amp + margin;
      const lo = ctx.bandTop + pad, hi = ctx.bandBottom - pad;
      const mid = ctx.bandTop + W * 0.5;

      // The stops are nominally fractions of total travel, which is where they
      // read best. When the configured band cannot hold them there they are
      // pulled inside it instead -- a stop above the actuation point or below
      // the release point would put the analog switch in a zone where rapid
      // trigger is not even running, and demonstrate nothing.
      const at = f => {
        if (hi <= lo) return mid;
        const want = clamp(f * ctx.span, lo, hi);
        return clearOfThreshold(want, amp, ctx.regularActuation, lo, hi);
      };
      const STOPS = [0.35, 0.55, 0.75].map(at);

      const CYCLES = 3;
      const DOWN = 0.7, WIGGLE = 1.2, STEP = 0.5, SINK = 0.5, RETURN = 1.0;

      const legs = [];
      let prev = 0;
      STOPS.forEach((d, i) => {
        const from = prev;                       // captured per iteration
        legs.push({ dur: i === 0 ? DOWN : STEP, from, to: d });
        legs.push({ dur: WIGGLE, from: d, to: d, amp });
        prev = d;
      });
      legs.push({ dur: SINK, from: prev, to: ctx.span });
      legs.push({ dur: RETURN, from: ctx.span, to: 0 });

      let x = t % legs.reduce((sum, l) => sum + l.dur, 0);
      for (const l of legs) {
        if (x < l.dur) {
          const u = x / l.dur;
          // Whole cycles, so each wiggle ends exactly where it began and the
          // next leg has nothing to step over.
          const d = l.amp
            ? l.from + l.amp * Math.sin(2 * Math.PI * CYCLES * u)
            : l.from + (l.to - l.from) * smoothstep(u);
          return clamp(d, 0, ctx.span);
        }
        x -= l.dur;
      }
      return 0;
    },
  },

  'rapid-trigger': {
    period: 5.5,
    blurb: 'Press to the middle of the rapid trigger band, wiggle there, then ease back to the top. The regular switch sees one press; rapid trigger fires on every wiggle.',
    depth: (t, ctx) => {
      const width = Math.max(0, ctx.bandBottom - ctx.bandTop);
      // A fifth of the band by preference, but never so small that it fails to
      // escape the sense zone, and never so large that it leaves the band.
      const amp = wiggleAmp(0.20 * width, ctx.sense,
        wiggleRoom(0.42, width, ctx, ctx.bandTop, ctx.bandBottom));
      const mid = clearOfThreshold(ctx.bandTop + width * 0.5, amp,
                                   ctx.regularActuation,
                                   ctx.bandTop + amp, ctx.bandBottom - amp);

      // Press down, hold and wiggle, then a slower and smoother return.
      const PRESS = 0.9, WIGGLE = 3.0, RETURN = 1.6, CYCLES = 6;
      const x = t % (PRESS + WIGGLE + RETURN);

      let d;
      if (x < PRESS) {
        d = mid * smoothstep(x / PRESS);
      } else if (x < PRESS + WIGGLE) {
        // Whole number of cycles, so the wiggle starts and ends exactly at mid
        // and the return leg has nothing to jump over.
        const tau = (x - PRESS) / WIGGLE;
        d = mid + amp * Math.sin(2 * Math.PI * CYCLES * tau);
      } else {
        d = mid * (1 - smoothstep((x - PRESS - WIGGLE) / RETURN));
      }
      return clamp(d, 0, ctx.span);
    },
  },

  'deep-wiggle': {
    period: 7.2,
    blurb: 'The same quick wiggle in two places. Below the release point nothing happens — the key is held on. Repeat it in the rapid trigger band and it fires on every stroke.',
    depth: (t, ctx) => {
      const W = Math.max(0, ctx.bandBottom - ctx.bandTop);

      // The always-on zone: between the release point and bottom-out.
      const deepH = Math.max(0, ctx.bottom - ctx.bandBottom);
      const deepMid = ctx.bandBottom + deepH * 0.5;

      // ONE amplitude for both legs. The claim this preset makes is that the
      // *same* stroke does nothing below the release point and fires
      // repeatedly inside the band, so the two legs must not be sized
      // independently. It has to fit in the narrower of the two zones and
      // still beat the sense zone.
      // The band leg also has to dodge the regular threshold, and since both
      // legs share one amplitude that constraint applies to the deep leg too.
      const amp = wiggleAmp(Math.max(0.35 * deepH, 0.12 * W), ctx.sense,
        Math.min(0.42 * deepH,
                 wiggleRoom(0.42, W, ctx, ctx.bandTop, ctx.bandBottom)));
      const deepAmp = amp, bandAmp = amp;

      const bandMid = clearOfThreshold(ctx.bandTop + W * 0.5, amp,
                                       ctx.regularActuation,
                                       ctx.bandTop + amp, ctx.bandBottom - amp);

      // Each leg starts exactly where the previous one ended, and each wiggle
      // runs a whole number of cycles, so the loop has no steps in it.
      const CYCLES = 8;   // 4 Hz over each 2 s wiggle leg
      const LEGS = [
        { dur: 1.0, at: u => deepMid * smoothstep(u) },                                  // press in
        { dur: 2.0, at: u => deepMid + deepAmp * Math.sin(2 * Math.PI * CYCLES * u) },   // wiggle below release
        { dur: 1.0, at: u => deepMid + (bandMid - deepMid) * smoothstep(u) },            // rise to the band
        { dur: 2.0, at: u => bandMid + bandAmp * Math.sin(2 * Math.PI * CYCLES * u) },   // same wiggle, in the band
        { dur: 1.2, at: u => bandMid * (1 - smoothstep(u)) },                            // back to the top
      ];

      let x = t % LEGS.reduce((sum, leg) => sum + leg.dur, 0);
      for (const leg of LEGS) {
        if (x < leg.dur) return clamp(leg.at(x / leg.dur), 0, ctx.span);
        x -= leg.dur;
      }
      return 0;
    },
  },

  manual: {
    period: null,
    blurb: 'Drag anywhere on the canvas to drive the key yourself.',
    depth: () => 0,
  },
};

// --- The world: one signal, two independently configured switches ----------
class World {
  constructor() {
    this.regular = new RegularSwitch({
      travel: 3.4,
      actuation: 1.2,
    });
    this.analog = new AnalogSwitch({
      travel: 3.4,
      actuation: 0.7,
      release: 3.0,
      sense: 0.060,
    });
    this.motionKey = 'multi-actuation';
    this.speed = 1.0;
    this.manualDepth = 0;
    this.t = 0;
    this.depth = 0;
    this._accum = 0;
    this.STEP = 0.001; // real boards poll at ~1 kHz
  }

  get motion() { return MOTIONS[this.motionKey]; }

  // Deepest travel of either switch -- sets the shared mm-to-pixel scale so
  // the two bars stay directly comparable.
  get maxTravel() {
    return Math.max(this.regular.params.travel, this.analog.params.travel);
  }

  // The stage a motion preset performs on. Built from configured parameters
  // only -- never from live switch state, so the finger can't react to what
  // the switches are doing.
  motionContext() {
    return {
      span: this.maxTravel,
      bandTop: this.analog.params.actuation,
      bandBottom: this.analog.releasePoint,
      bottom: this.analog.params.travel,
      sense: this.analog.effectiveSense,
      regularActuation: this.regular.params.actuation,
    };
  }

  resetRun() {
    this.t = 0;
    this._accum = 0;
    this.regular.reset();
    this.analog.reset();
  }

  // Advance by `dt` seconds of wall clock, sub-stepping at a fixed 1 ms so a
  // dropped frame changes what you see but never what gets counted.
  advance(dt) {
    const ctx = this.motionContext();
    this._accum += Math.min(dt, 0.25) * this.speed;
    while (this._accum >= this.STEP) {
      this._accum -= this.STEP;
      this.t += this.STEP;
      this.depth = this.motionKey === 'manual'
        ? this.manualDepth
        : this.motion.depth(this.t, ctx);
      this.regular.step(this.depth);
      this.analog.step(this.depth);
    }
  }
}

window.RT = {
  MAX_TRAVEL, RELEASE_GAP, clamp,
  RegularSwitch, AnalogSwitch, MOTIONS, World,
};
